/**
 * SizilPro — Sidebar
 * Left navigation for all modules. Collapsible.
 * Active state driven by TanStack Router's useRouterState.
 */

import { useRouterState, Link } from "@tanstack/react-router";
import {
  BookOpen,
  FileText,
  LayoutTemplate,
  ChevronLeft,
  ChevronRight,
  Settings,
  LogOut,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useUiStore } from "@/store/uiStore";
import { useAuthStore } from "@/store/authStore";

type AppRouteHref =
  | "/proposal-builder"
  | "/proposal-builder/templates"
  | "/settings";

type NavSubItem =
  | {
      label: string;
      icon: typeof LayoutTemplate;
      href: AppRouteHref;
      kind: "route";
    }
  | {
      label: string;
      icon: typeof LayoutTemplate;
      href: string;
      kind: "external";
    };

type NavItem =
  | {
      label: string;
      icon: typeof BookOpen;
      href: string;
      kind: "external";
      sub: readonly NavSubItem[];
    }
  | {
      label: string;
      icon: typeof FileText;
      href: AppRouteHref;
      kind: "route";
      sub: readonly NavSubItem[];
    };

// ─── Nav structure ────────────────────────────────────────────
// Top-level modules + sub-items that appear indented under them.

const NAV: readonly NavItem[] = [
  {
    label: "Bookkeeping",
    icon: BookOpen,
    href: "/bookkeeping",
    kind: "external",
    sub: [],
  },
  {
    label: "Proposals",
    icon: FileText,
    href: "/proposal-builder",
    kind: "route",
    sub: [
      {
        label: "Templates",
        icon: LayoutTemplate,
        href: "/proposal-builder/templates",
        kind: "route",
      },
    ],
  },
] as const;

function NavLinkContent({
  icon: Icon,
  label,
  open,
  small = false,
}: {
  icon: typeof BookOpen | typeof FileText | typeof LayoutTemplate | typeof Settings;
  label: string;
  open: boolean;
  small?: boolean;
}) {
  return (
    <>
      <Icon className={cn("shrink-0", small ? "w-3.5 h-3.5" : "w-4 h-4")} />
      {open && <span className="truncate">{label}</span>}
    </>
  );
}

export function Sidebar() {
  const open = useUiStore((s) => s.sidebarOpen);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const signOut = useAuthStore((s) => s.signOut);
  const name = useAuthStore((s) => s.name);

  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 bottom-0 z-30 flex flex-col border-r border-white/10 bg-[#0a0a0a] transition-all duration-200",
        open ? "w-56" : "w-14"
      )}
    >
      <div className="flex items-center gap-3 px-4 h-14 border-b border-white/10 shrink-0">
        <div className="w-6 h-6 rounded-md bg-white flex items-center justify-center shrink-0">
          <span className="text-black font-black text-xs leading-none">S</span>
        </div>
        {open && (
          <span className="text-sm font-bold tracking-tight truncate">SizilPro</span>
        )}
      </div>

      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto">
        {NAV.map((item) => {
          const isActive = pathname.startsWith(item.href);
          const showSub = isActive && open && item.sub.length > 0;
          const parentClasses = cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all",
            isActive
              ? "bg-white/10 text-white font-medium"
              : "text-white/40 hover:text-white hover:bg-white/5"
          );

          return (
            <div key={item.href}>
              {item.kind === "route" ? (
                <Link to={item.href} className={parentClasses}>
                  <NavLinkContent
                    icon={item.icon}
                    label={item.label}
                    open={open}
                  />
                </Link>
              ) : (
                <a href={item.href} className={parentClasses}>
                  <NavLinkContent
                    icon={item.icon}
                    label={item.label}
                    open={open}
                  />
                </a>
              )}

              {showSub && (
                <div className="mt-0.5 ml-3 pl-3 border-l border-white/[0.06] space-y-0.5">
                  {item.sub.map((sub) => {
                    const subActive = pathname.startsWith(sub.href);
                    const subClasses = cn(
                      "flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all",
                      subActive
                        ? "bg-white/10 text-white font-medium"
                        : "text-white/30 hover:text-white/70 hover:bg-white/5"
                    );

                    return sub.kind === "route" ? (
                      <Link key={sub.href} to={sub.href} className={subClasses}>
                        <NavLinkContent
                          icon={sub.icon}
                          label={sub.label}
                          open={true}
                          small
                        />
                      </Link>
                    ) : (
                      <a key={sub.href} href={sub.href} className={subClasses}>
                        <NavLinkContent
                          icon={sub.icon}
                          label={sub.label}
                          open={true}
                          small
                        />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="border-t border-white/10 px-2 py-3 space-y-0.5">
        <Link
          to="/settings"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all",
            pathname.startsWith("/settings")
              ? "bg-white/10 text-white/70 font-medium"
              : "text-white/30 hover:text-white hover:bg-white/5"
          )}
        >
          <NavLinkContent icon={Settings} label="Settings" open={open} />
        </Link>

        <button
          type="button"
          onClick={() => signOut()}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/30 hover:text-white hover:bg-white/5 transition-all"
        >
          <LogOut className="w-4 h-4 shrink-0" />
          {open && <span className="truncate">{name ?? "Sign out"}</span>}
        </button>
      </div>

      <button
        type="button"
        onClick={toggle}
        className={cn(
          "absolute -right-3 top-1/2 -translate-y-1/2",
          "w-6 h-6 rounded-full bg-[#1a1a1a] border border-white/15",
          "flex items-center justify-center",
          "text-white/40 hover:text-white transition-colors shadow-lg"
        )}
      >
        {open ? (
          <ChevronLeft className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
      </button>
    </aside>
  );
}